import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * Area `workspace` (plan-014 additions) of the console gateway — over `apps/api/src/routes/workspace/` (design
 * §27.9.2). Owned by plan-014 unit **B3**. The pre-plan-014 workspace methods (`listWorkspaces`, `getWorkspace`, the
 * people acts) stay where they were; these are what the Workspaces and Workspace detail screens add:
 *
 *   readWorkspaceCards             `listWorkspaces`               cards with funding, revision and the caller's role (?q=, keyset)
 *   readWorkspaceManagement        `getWorkspace`                 one workspace's management facts, with its monthly cap
 *   createWorkspaceInAccount       `createAccountWorkspace`       name, optional slug, time zone, shared or own balance, kind
 *   changeWorkspaceKind            `updateWorkspace`              plan-015 WSK (G25): Standard ↔ Test (the organization's Owner or Admin)
 *   readWorkspaceAccessPolicy      `getWorkspaceAccessPolicy`     how people join
 *   changeWorkspaceAccessPolicy    `putWorkspaceAccessPolicy`     (If-Match)
 *   readWorkspaceCreationPolicy    `getWorkspaceCreationPolicy`   who may create workspaces
 *   changeWorkspaceCreationPolicy  `putWorkspaceCreationPolicy`   (If-Match)
 *   signUpFromInvitation           `signUpPerson`                 the invitation page's "Create a sign-in" (owner answer 45)
 *
 * ── THE GENERATED CLIENT ─────────────────────────────────────────────────────────────────────────────────────
 * The operations are in B3's fragments; the merged spec and `@wetalk/api-client` are regenerated by the api-client lock
 * holder (plan-014 §2.3), never by a unit. So this slice reads the client through {@link operationsOf}, a structural
 * view with exactly these methods (B2's and C1's precedent): it compiles before and after the regeneration, and a
 * client that still lacks one raises naming the regeneration — never an empty answer.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";
import { MEMBER_ROLE, money, type CurrencyCode, type MemberRole, type Money } from "@wetalk/domain";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";
import { spendCapOfWire, type SpendCapView } from "./entitlement.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

export type WorkspaceFundingKind = "shared" | "dedicated";

export interface WorkspaceFundingView {
  readonly kind: WorkspaceFundingKind;
  readonly billingAccountId: string;
  /** "Shared balance", "Trastevere balance"; `null` before the organization was migrated. */
  readonly billingAccountName: string | null;
  /** "since 3 Oct 2026", or `null` when the funding carries no date yet (before the migration). */
  readonly sinceText: string | null;
}

export type WorkspaceLifecycleState = "active" | "draining" | "archived";

/**
 * plan-015 WSK (G25, ADR 0050): what a workspace is for — `standard`, or `test` (a test or staging workspace). A label
 * only: billed like any workspace. The list draws the flask and "Test" for `test`.
 */
export type WorkspaceKind = "standard" | "test";

export const WORKSPACE_KIND: readonly WorkspaceKind[] = ["standard", "test"];

export interface WorkspaceCardView {
  readonly workspaceId: string;
  readonly name: string;
  readonly initials: string;
  readonly slug: string;
  readonly timeZone: string;
  readonly state: WorkspaceLifecycleState;
  /** plan-015 WSK (G25): Standard or Test — a label, billed normally. */
  readonly kind: WorkspaceKind;
  readonly memberCount: number;
  readonly voiceAgentCount: number;
  /** This month's talk time in whole seconds, today included — shown through `talkTimeText`. */
  readonly monthTalkSecond: number;
  readonly monthSpend: Money;
  readonly funding: WorkspaceFundingView;
  /**
   * plan-015 BAL (G24): the settled amount of the balance of its own that funds it — only when the API served it, which
   * it does for who may read that balance (the organization's Owner, Admin and Finance, or a delegate of it); `null`
   * otherwise (shared funding, or a reader without that right), and the row reads "own balance" with no amount.
   */
  readonly dedicatedBalance: Money | null;
  /** The If-Match of a rename. */
  readonly revision: number;
  /** The caller's own role in it, or `null` (an organization role sees every workspace). */
  readonly memberRole: MemberRole | null;
  /** "23 Sep 2026". */
  readonly createdAtText: string;
}

export interface WorkspaceCardPageView {
  readonly workspace: readonly WorkspaceCardView[];
  readonly nextCursor: string | null;
}

/**
 * The workspace's monthly cap as its page reads it: the full spend cap (amount, behaviour at it, overage, warning and
 * the revision a change names — INT 2026-10-07, so a workspace Owner or Admin without organization finance can edit an
 * existing cap), with B3's two derived facts.
 */
export interface WorkspaceSpendCapView extends SpendCapView {
  readonly monthBudget: Money;
  /** The cap stops new conversations at some point (`capBehavior` is not `notify_only`). */
  readonly hardStop: boolean;
}

export interface WorkspaceManagementView extends WorkspaceCardView {
  /** The workspace's monthly cap (its budget), or `null` for none. */
  readonly spendCap: WorkspaceSpendCapView | null;
}

/** B3's two derived facts beside the full cap. */
export function spendCapOfView(cap: SpendCapView): WorkspaceSpendCapView {
  return { ...cap, monthBudget: money(cap.monthBudgetMinor, cap.currency as CurrencyCode), hardStop: cap.capBehavior !== "notify_only" };
}

export interface WorkspaceDraftView {
  readonly name: string;
  /** The address the person chose, or `null` to derive one from the name. */
  readonly slug: string | null;
  readonly timeZone: string;
  readonly funding: WorkspaceFundingKind;
  /** plan-015 WSK (G25): the dialog's "What it is for" — a test or staging workspace is `test`. */
  readonly kind: WorkspaceKind;
}

/** plan-015 WSK (G25): a relabel names the workspace's name and zone as they stand (the PATCH body) and its revision. */
export interface WorkspaceKindChangeView {
  readonly name: string;
  readonly timeZone: string;
  readonly kind: WorkspaceKind;
  readonly revision: number;
}

export type WorkspaceJoinMode = "invitation_only" | "verified_domain";

export interface WorkspaceAccessPolicyView {
  readonly joinMode: WorkspaceJoinMode;
  readonly domainJoinRole: MemberRole | null;
  readonly ssoDomainId: string | null;
  readonly domain: string | null;
  readonly revision: number;
  /** The verified domains a rule could name — for whoever may change it; `null` for a reader. */
  readonly verifiedDomain: readonly { readonly ssoDomainId: string; readonly domain: string }[] | null;
}

export interface WorkspaceAccessPolicyChangeView {
  readonly joinMode: WorkspaceJoinMode;
  readonly domainJoinRole: MemberRole | null;
  readonly ssoDomainId: string | null;
}

export type WorkspaceCreationPolicy = "owner_and_admin" | "owner_only";

export interface WorkspaceCreationPolicyView {
  readonly policy: WorkspaceCreationPolicy;
  readonly revision: number;
  /** Whether the caller may change it now. */
  readonly canChange: boolean;
}

/**
 * The invitation page's "Create my sign-in". AMENDED 2026-11-03 (owner decision; design §16.37 amendment): a `code` —
 * the one `requestInvitationSignInCode` mailed to the invited address — instead of a `password`; exactly one of the two.
 */
export interface InvitationSignUpView {
  readonly email: string;
  readonly displayName: string;
  readonly password: string | null;
  readonly code: string | null;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The workspaces the caller may see, as cards: funding, revision, their own role (`q` searches the name). */
    readWorkspaceCards(search: { readonly q: string | null; readonly cursor: string | null }): Promise<WorkspaceCardPageView>;
    /** One workspace's management facts — funding, revision, the caller's role and its monthly cap. */
    readWorkspaceManagement(workspaceId: string): Promise<WorkspaceManagementView>;
    /** Creates a workspace in the organization, on the shared balance or a balance of its own. */
    createWorkspaceInAccount(accountId: string, draft: WorkspaceDraftView): Promise<WorkspaceCardView>;
    /**
     * plan-015 WSK (G25): marks a workspace Standard or Test — the organization's Owner or Admin only (the API judges
     * `account.workspace.manage` on it), audited `workspace.kind_changed`. If-Match is the revision the screen read.
     */
    changeWorkspaceKind(workspaceId: string, change: WorkspaceKindChangeView): Promise<WorkspaceCardView>;
    readWorkspaceAccessPolicy(workspaceId: string): Promise<WorkspaceAccessPolicyView>;
    changeWorkspaceAccessPolicy(workspaceId: string, change: WorkspaceAccessPolicyChangeView, revision: number): Promise<WorkspaceAccessPolicyView>;
    readWorkspaceCreationPolicy(accountId: string): Promise<WorkspaceCreationPolicyView>;
    changeWorkspaceCreationPolicy(accountId: string, policy: WorkspaceCreationPolicy, revision: number): Promise<WorkspaceCreationPolicyView>;
    /**
     * Creates a sign-in from an invitation page: the person only, no organization; the invited address is verified
     * by the token and no verification mail is sent (owner answer 45). The browser holds the new sign-in afterwards.
     */
    signUpFromInvitation(token: string, signUp: InvitationSignUpView): Promise<void>;
  }
}

export type WorkspaceManagementGatewaySlice = Pick<
  ConsoleGateway,
  | "readWorkspaceCards"
  | "readWorkspaceManagement"
  | "createWorkspaceInAccount"
  | "changeWorkspaceKind"
  | "readWorkspaceAccessPolicy"
  | "changeWorkspaceAccessPolicy"
  | "readWorkspaceCreationPolicy"
  | "changeWorkspaceCreationPolicy"
  | "signUpFromInvitation"
>;

/** Nothing refused: every operation is in B3's fragments (served once the client is regenerated). */
export const workspaceManagementGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────

type Header = Readonly<Record<string, string>>;

interface WorkspaceManagementOperations {
  listWorkspaces(request: { query?: Readonly<Record<string, string>> }): Promise<ApiResult>;
  getWorkspace(request: { path: { workspace_id: string } }): Promise<ApiResult>;
  createAccountWorkspace(request: { path: { account_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  updateWorkspace(request: { path: { workspace_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  getWorkspaceAccessPolicy(request: { path: { workspace_id: string } }): Promise<ApiResult>;
  putWorkspaceAccessPolicy(request: { path: { workspace_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  getWorkspaceCreationPolicy(request: { path: { account_id: string } }): Promise<ApiResult>;
  putWorkspaceCreationPolicy(request: { path: { account_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  signUpPerson(request: { body?: unknown }): Promise<ApiResult>;
}

/** The client's methods for this area, by name. Raises for one the generated client does not carry yet. */
function operationsOf(client: WeTalkApiClient): WorkspaceManagementOperations {
  const held = client as unknown as Partial<Record<keyof WorkspaceManagementOperations, unknown>>;
  return new Proxy({} as WorkspaceManagementOperations, {
    get(_target, name: string) {
      const found = held[name as keyof WorkspaceManagementOperations];
      if (typeof found !== "function") {
        throw new Error(
          portalText("shell", "the_generated_api_client_has_no_value_regenerate_it_pnpm_a_bb0ec95e", { v0: String(name) }),
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding (exported for the sandbox half and the suites) ──────────────────────────────────────────────

function dataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(assertOk(result), operationId)["data"], `${operationId}.data`);
}

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function roleOrNull(value: unknown, path: string): MemberRole | null {
  if (value === null || value === undefined) return null;
  const raw = readText(value, path);
  const found = MEMBER_ROLE.find((one) => one === raw);
  if (found === undefined) throw new Error(`${path}: "${raw}" is not a workspace role`);
  return found;
}

/** "Roma Pizzeria" → "RP"; a name with no letters is "WT". */
export function initialsOfWorkspace(name: string): string {
  const part = name
    .split(/[^\p{L}\p{N}]+/u)
    .filter((one) => one !== "")
    .slice(0, 2);
  if (part.length === 0) return "WT";
  return part.map((one) => one.slice(0, 1).toUpperCase()).join("");
}

/** "23 Sep 2026". */
export function dayText(instant: string): string {
  return new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { day: "numeric", month: "short", year: "numeric" }).format(new Date(instant));
}

function lifecycleStateOf(value: unknown, path: string): WorkspaceLifecycleState {
  const raw = readText(value, path);
  if (raw === "active" || raw === "draining" || raw === "archived") return raw;
  throw new Error(`${path}: "${raw}" is not a workspace state`);
}

/** `kind` — a value this console does not know is a decoding fault, never drawn as either label. */
export function workspaceKindOf(value: unknown, path: string): WorkspaceKind {
  const raw = readText(value, path);
  const found = WORKSPACE_KIND.find((one) => one === raw);
  if (found === undefined) throw new Error(`${path}: "${raw}" is not a workspace kind`);
  return found;
}

function fundingOf(value: unknown, path: string): WorkspaceFundingView {
  const one = readRecord(value, path);
  const kind = readText(one["kind"], `${path}.kind`);
  if (kind !== "shared" && kind !== "dedicated") throw new Error(`${path}.kind: "${kind}" is not a funding kind`);
  const since = textOrNull(one["since"], `${path}.since`);
  return validatedDisplayView({
    kind,
    billingAccountId: readText(one["billing_account_id"], `${path}.billing_account_id`),
    billingAccountName: textOrNull(one["billing_account_name"], `${path}.billing_account_name`),
    get sinceText() { return since === null ? null : portalText("shell", "since_value_382b0256", { v0: String(dayText(since)) }); },
  });
}

/** `dedicated_balance` — present only for a reader of that balance (plan-015 BAL); absent is `null`. */
function dedicatedBalanceOf(value: unknown, path: string): Money | null {
  if (value === undefined || value === null) return null;
  const one = readRecord(value, path);
  return money(readWholeNumber(one["amount_minor"], `${path}.amount_minor`), readText(one["currency"], `${path}.currency`) as CurrencyCode);
}

export function workspaceCardOfWire(one: Readonly<Record<string, unknown>>): WorkspaceCardView {
  const name = readText(one["name"], "workspace.name");
  const currency = readText(one["currency"], "workspace.currency") as CurrencyCode;
  return validatedDisplayView({
    workspaceId: readText(one["workspace_id"], "workspace.workspace_id"),
    name,
    initials: initialsOfWorkspace(name),
    slug: readText(one["slug"], "workspace.slug"),
    timeZone: readText(one["time_zone"], "workspace.time_zone"),
    state: lifecycleStateOf(one["state"], "workspace.state"),
    kind: workspaceKindOf(one["kind"], "workspace.kind"),
    memberCount: readWholeNumber(one["member_count"], "workspace.member_count"),
    voiceAgentCount: readWholeNumber(one["voice_agent_count"], "workspace.voice_agent_count"),
    monthTalkSecond: readWholeNumber(one["month_billed_second"], "workspace.month_billed_second"),
    monthSpend: money(readWholeNumber(one["month_priced_minor"], "workspace.month_priced_minor"), currency),
    funding: fundingOf(one["funding"], "workspace.funding"),
    dedicatedBalance: dedicatedBalanceOf(one["dedicated_balance"], "workspace.dedicated_balance"),
    revision: readWholeNumber(one["revision"], "workspace.revision"),
    memberRole: roleOrNull(one["member_role"], "workspace.member_role"),
    get createdAtText() { return dayText(readText(one["created_at"], "workspace.created_at")); },
  });
}

export function workspaceManagementOfWire(one: Readonly<Record<string, unknown>>): WorkspaceManagementView {
  const card = workspaceCardOfWire(one);
  const cap = one["spend_cap"];
  if (cap === null || cap === undefined) return { ...card, spendCap: null };
  return { ...card, spendCap: spendCapOfView(spendCapOfWire(readRecord(cap, "workspace.spend_cap"), "workspace.spend_cap")) };
}

function accessPolicyOfWire(one: Readonly<Record<string, unknown>>): WorkspaceAccessPolicyView {
  const mode = readText(one["join_mode"], "accessPolicy.join_mode");
  if (mode !== "invitation_only" && mode !== "verified_domain") throw new Error(`accessPolicy.join_mode: "${mode}" is not a join mode`);
  const domains = one["verified_domain"];
  return {
    joinMode: mode,
    domainJoinRole: roleOrNull(one["domain_join_role"], "accessPolicy.domain_join_role"),
    ssoDomainId: textOrNull(one["sso_domain_id"], "accessPolicy.sso_domain_id"),
    domain: textOrNull(one["domain"], "accessPolicy.domain"),
    revision: readWholeNumber(one["revision"], "accessPolicy.revision"),
    verifiedDomain:
      domains === null || domains === undefined
        ? null
        : readArray(domains, "accessPolicy.verified_domain").map((each, index) => {
            const row = readRecord(each, `accessPolicy.verified_domain[${String(index)}]`);
            return {
              ssoDomainId: readText(row["sso_domain_id"], "verifiedDomain.sso_domain_id"),
              domain: readText(row["domain"], "verifiedDomain.domain"),
            };
          }),
  };
}

function creationPolicyOfWire(one: Readonly<Record<string, unknown>>): WorkspaceCreationPolicyView {
  const policy = readText(one["workspace_creation_policy"], "creationPolicy.workspace_creation_policy");
  if (policy !== "owner_and_admin" && policy !== "owner_only") throw new Error(`creationPolicy: "${policy}" is not a policy`);
  return {
    policy,
    revision: readWholeNumber(one["revision"], "creationPolicy.revision"),
    canChange: readBoolean(one["can_change"], "creationPolicy.can_change"),
  };
}

function ifMatch(revision: number): Header {
  return { "if-match": `"${String(revision)}"` };
}

export function workspaceManagementApiSlice(client: WeTalkApiClient, _context: SliceContext): WorkspaceManagementGatewaySlice {
  const api = (): WorkspaceManagementOperations => operationsOf(client);
  return {
    async readWorkspaceCards(search) {
      const query: Record<string, string> = {};
      if (search.q !== null && search.q.trim() !== "") query["q"] = search.q.trim();
      if (search.cursor !== null) query["cursor"] = search.cursor;
      // A client generated before B3's fragment sends no query at all; a search it would silently drop is refused.
      const listing = (client as unknown as { readonly listWorkspaces?: { readonly length: number } }).listWorkspaces;
      if (Object.keys(query).length > 0 && listing !== undefined && listing.length === 0) {
        throw new Error("The generated API client cannot search workspaces yet. Regenerate it (pnpm api:build-spec) so it carries B3's query.");
      }
      const body = readRecord(assertOk(await api().listWorkspaces({ query })), "listWorkspaces");
      return {
        workspace: readArray(body["data"], "listWorkspaces.data").map((one, index) => workspaceCardOfWire(readRecord(one, `workspace[${index}]`))),
        nextCursor: textOrNull(body["next_cursor"], "listWorkspaces.next_cursor"),
      };
    },
    async readWorkspaceManagement(workspaceId) {
      return workspaceManagementOfWire(dataOf(await api().getWorkspace({ path: { workspace_id: workspaceId } }), "getWorkspace"));
    },
    async createWorkspaceInAccount(accountId, draft) {
      const body: Record<string, unknown> = { name: draft.name, time_zone: draft.timeZone, funding: { kind: draft.funding }, kind: draft.kind };
      if (draft.slug !== null) body["slug"] = draft.slug;
      return workspaceCardOfWire(
        dataOf(await api().createAccountWorkspace({ path: { account_id: accountId }, body }), "createAccountWorkspace"),
      );
    },
    async changeWorkspaceKind(workspaceId, change) {
      return workspaceCardOfWire(
        dataOf(
          await api().updateWorkspace({
            path: { workspace_id: workspaceId },
            body: { name: change.name, time_zone: change.timeZone, kind: change.kind },
            header: ifMatch(change.revision),
          }),
          "updateWorkspace",
        ),
      );
    },
    async readWorkspaceAccessPolicy(workspaceId) {
      return accessPolicyOfWire(dataOf(await api().getWorkspaceAccessPolicy({ path: { workspace_id: workspaceId } }), "getWorkspaceAccessPolicy"));
    },
    async changeWorkspaceAccessPolicy(workspaceId, change, revision) {
      return accessPolicyOfWire(
        dataOf(
          await api().putWorkspaceAccessPolicy({
            path: { workspace_id: workspaceId },
            body: { join_mode: change.joinMode, domain_join_role: change.domainJoinRole, sso_domain_id: change.ssoDomainId },
            header: ifMatch(revision),
          }),
          "putWorkspaceAccessPolicy",
        ),
      );
    },
    async readWorkspaceCreationPolicy(accountId) {
      return creationPolicyOfWire(dataOf(await api().getWorkspaceCreationPolicy({ path: { account_id: accountId } }), "getWorkspaceCreationPolicy"));
    },
    async changeWorkspaceCreationPolicy(accountId, policy, revision) {
      return creationPolicyOfWire(
        dataOf(
          await api().putWorkspaceCreationPolicy({
            path: { account_id: accountId },
            body: { workspace_creation_policy: policy },
            header: ifMatch(revision),
          }),
          "putWorkspaceCreationPolicy",
        ),
      );
    },
    async signUpFromInvitation(token, signUp) {
      assertOk(
        await api().signUpPerson({
          body: {
            invitation_token: token,
            email: signUp.email,
            display_name: signUp.displayName,
            ...(signUp.password === null ? {} : { password: signUp.password }),
            ...(signUp.code === null ? {} : { code: signUp.code }),
          },
        }),
      );
    },
  };
}
